import { EnumLanguage } from '@libs/constants';
import { Util } from '@libs/utilities/util';

import { IPodDesign } from '../../contracts/pod-design.interface';
import { IGlobalText } from '../../contracts/pod-listing.interface';
import { EnumPlatform } from '../../contracts/print-on-demand.constants';
import { IListingCheck, IPlatformStatus, PLACEHOLDERS } from '../data/listing-check.interface';


export class GlobalListingCheckHelper implements IListingCheck {


	/**------------------------------------------------------
	 * Check for empty listing data
	 */
	isListingEmpty(data: IPodDesign): boolean {

		//0 - is any global data available
		if (data?.listing?.global === null || typeof data?.listing?.global === 'undefined') return true;

		//1 - is any listing text available
		for (const text of data.listing.global.texts) {
			if (!Util.String.areEmpty([text.title, text.description, text.keywords.join('')])) return false;
		}

		//2 - all listing text was empty
		return true;
	}


	/**------------------------------------------------------
	 * Check the Listing Data
	 */
	checkListing(data: IPodDesign): IPlatformStatus[] {
		const statusGlobal: IPlatformStatus[] = [];

		//0 - check the statuses of all platform belonging to global
		// > if global is null the statuses will be initialized
		const isListingEmpty: boolean = this.isListingEmpty(data);
		statusGlobal.push(this.checkFineArtAmerica(data, isListingEmpty));
		statusGlobal.push(this.checkPrintful(data, 	 	isListingEmpty));
		statusGlobal.push(this.checkPrintify(data, 	 	isListingEmpty));
		statusGlobal.push(this.checkRedbubble(data,  	isListingEmpty));
		statusGlobal.push(this.checkShirtee(data, 		isListingEmpty));
		statusGlobal.push(this.checkSociety6(data, 	 	isListingEmpty));
		statusGlobal.push(this.checkSpreadshirtUs(data, isListingEmpty));
		statusGlobal.push(this.checkSpreadshirtEu(data, isListingEmpty));
		statusGlobal.push(this.checkTeepublic(data, 	isListingEmpty));
		statusGlobal.push(this.checkTeespring(data, 	isListingEmpty));
		statusGlobal.push(this.checkTostadora(data, 	isListingEmpty));
		statusGlobal.push(this.checkZazzle(data, 		isListingEmpty));

		//1 - check if any global data was defined
		if (isListingEmpty || !data.listing.global) return statusGlobal;

		//2 - check for any placeholders in the text
		for (const text of data.listing.global.texts) {
			for (const placeholder of PLACEHOLDERS) {
				for (const platformStatus of statusGlobal) {
					if (text.title.includes(placeholder)) 		platformStatus.warnings.push(`placeholder "${placeholder}" found in title (${text.language.toLowerCase()})`);
					if (text.description.includes(placeholder)) platformStatus.warnings.push(`placeholder "${placeholder}" found in description (${text.language.toLowerCase()})`);
					if (text.keywords.includes(placeholder))	platformStatus.warnings.push(`placeholder "${placeholder}" found in keywords (${text.language.toLowerCase()})`);
				}
			}
		}

		//3 - check if any types were configured (relevant for the templates)
		const platformsRequireTypes: string[] = [
			EnumPlatform.FineArtAmerica, EnumPlatform.Redbubble, EnumPlatform.Shirtee,   EnumPlatform.Society6,  EnumPlatform.SpreadshirtUS,
			EnumPlatform.SpreadshirtEU,  EnumPlatform.Teepublic, EnumPlatform.Teespring, EnumPlatform.Tostadora, EnumPlatform.Zazzle
		];
		if (data.listing.global.options.templateMapping.types.length === 0) {
			for (const status of statusGlobal) {
				if (platformsRequireTypes.includes(status.code)) status.errors.push('no type was defined');
			}
		}

		//4 - return the result
		return statusGlobal;
	}


	/**------------------------------------------------------
	 * Fine Art America
	 * ----------------
	 * Title:
	 *  - required, max 100 char, no special char allowed
	 * Description
	 *  - required, max 250 char
	 * Keywords
	 *  - required, max 500 char in total
	 */
	private checkFineArtAmerica(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.FineArtAmerica, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if english text is configured
		const textEn = this.getTextByLanguage(EnumLanguage.EN, data);
		if (!textEn) {
			status.errors.push(`text was not defined (en)`);
			return status;
		}

		//2 - check the title
		if (Util.String.isEmpty(textEn.title))		 	status.errors.push('title is empty (en)');
		if (textEn.title.length > 100)				 	status.warnings.push('title over 100 characters (en)');
		if (/[^a-z0-9A-Z \-,.'/]/g.test(textEn.title))  status.warnings.push('title contains special characters (en)');		// check for special chars in the title

		//3 - check description
		if (Util.String.isEmpty(textEn.description))	status.errors.push('description is empty (en)');
		if (textEn.description.length > 250)			status.warnings.push('description over 250 characters (en)');

		//4 - check the keywords
		if (textEn.keywords.length === 0) 				status.errors.push('keywords are empty (en)');
		if (textEn.keywords.join(', ').length > 500) 	status.warnings.push('keywords over 500 characters (en)');

		return status;
	}


	/**------------------------------------------------------
	 * Printful
	 * --------
	 * Title
	 *  - required, max 140 char, char '%' max 1 times
	 *  - product name might be added to title (only shopify)
	 * Description
	 *  - required
	 * Keywords
	 *  - max 13 keywords, max char length for kw 20 char
	 */
	private checkPrintful(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Printful, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if any language was created
		const validLanguages: EnumLanguage[] = [EnumLanguage.EN, EnumLanguage.DE, EnumLanguage.FR, EnumLanguage.ES];
		const textsPrintful = this.getTextsByLanguages(validLanguages, data);
		if (!textsPrintful) {
			status.errors.push(`text not defined for any language (${validLanguages.join(', ').toLowerCase()})`);
			return status;
		}

		//2 - check if the texts are alright
		for (const text of textsPrintful) {

			//a. check the title
			if (Util.String.isEmpty(text.title)) 				status.errors.push(`title is empty (${text.language.toLowerCase()})`);
			if (text.title.length > 140)						status.warnings.push(`title over 140 characters (${text.language.toLowerCase()})`);
			if (Util.String.isNotEmpty(text.title) && Util.Text.countOccurrence(text.title, '%') > 1) status.warnings.push(`title can only have one "%" symbol (${text.language.toLowerCase()})`);

			//b. check desc. & keywords
			if (Util.String.isEmpty(text.description))			status.errors.push(`description is empty (${text.language.toLowerCase()})`);
			if (text.keywords.length > 13)						status.warnings.push(`over max of 13 keywords (${text.language.toLowerCase()})`);
			if (text.keywords.length !== 0 && Util.Keywords.marginalLength(text.keywords).max > 20) status.warnings.push(`over 20 character in a keyword (${text.language.toLowerCase()})`);
		}

		return status;
	}


	/**------------------------------------------------------
	 * Printify
	 * --------
	 * Title
	 *  - required, max 140 char, char '%, &' max 2 times
	 *  - product name might be added to title (only shopify)
	 * Description
	 *  - required
	 */
	private checkPrintify(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Printify, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if any language was created
		const validLanguages: EnumLanguage[] = [EnumLanguage.EN, EnumLanguage.DE, EnumLanguage.FR, EnumLanguage.ES];
		const textsPrintful = this.getTextsByLanguages(validLanguages, data);
		if (!textsPrintful) {
			status.errors.push(`text not defined for any language (${validLanguages.join(', ').toLowerCase()})`);
			return status;
		}

		//2 - check if the texts are alright
		for (const text of textsPrintful) {

			//a. check the title
			if (Util.String.isEmpty(text.title)) 		status.errors.push(`title is empty (${text.language.toLowerCase()})`);
			if (text.title.length > 140)				status.warnings.push(`title over 140 characters (${text.language.toLowerCase()})`);
			if (Util.String.isNotEmpty(text.title)) {
				if (Util.Text.countOccurrence(text.title, '%') > 1) status.warnings.push(`title can only have one "%" symbol (${text.language.toLowerCase()})`);
				if (Util.Text.countOccurrence(text.title, '&') > 1) status.warnings.push(`title can only have one "&" symbol (${text.language.toLowerCase()})`);
			}

			//b. check desc. & keywords
			if (Util.String.isEmpty(text.description))	status.errors.push(`description is empty (${text.language.toLowerCase()})`);
		}

		return status;
	}


	/**------------------------------------------------------
	 * Redbubble
	 * --------
	 * Title
	 *  - required
	 * Description
	 *  - max 2000
	 * Keywords
	 *  - required, keyword max 50 char
	 */
	private checkRedbubble(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Redbubble, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if any language was created
		const validLanguages: EnumLanguage[] = [EnumLanguage.EN, EnumLanguage.DE, EnumLanguage.FR, EnumLanguage.ES];
		const textsRedbubble = this.getTextsByLanguages(validLanguages, data);
		if (!textsRedbubble) {
			status.errors.push(`text not defined for any language (${validLanguages.join(', ').toLowerCase()})`);
			return status;
		}

		//2 - check if the texts are alright
		for (const text of textsRedbubble) {

			//a. check title & description
			if (Util.String.isEmpty(text.title)) status.errors.push(`title is empty (${text.language.toLowerCase()})`);
			if (text.description.length > 2000)	 status.warnings.push(`description over 2000 characters (${text.language.toLowerCase()})`);

			//b. check keywords
			if (text.keywords.length === 0) status.errors.push(`keywords are empty (${text.language.toLowerCase()})`);
			if (text.keywords.length > 0 && Util.Keywords.marginalLength(text.keywords).max > 50) status.warnings.push(`over 50 character in a keyword (${text.language.toLowerCase()})`);
		}

		return status;
	}


	/**------------------------------------------------------
	 * Shirtee
	 * -------
	 * Title
	 *  - required, max 40 char
	 * Description
	 *  - required
	 * Keywords
	 *  - required, max 10 keywords
	 */
	private checkShirtee(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Shirtee, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if any language was created
		const validLanguages: EnumLanguage[] = [EnumLanguage.EN, EnumLanguage.DE, EnumLanguage.FR, EnumLanguage.ES];
		const textsRedbubble = this.getTextsByLanguages(validLanguages, data);
		if (!textsRedbubble) {
			status.errors.push(`text not defined for any language (${validLanguages.join(', ').toLowerCase()})`);
			return status;
		}

		//2 - check if the texts are alright
		for (const text of textsRedbubble) {

			//a. check the title
			if (Util.String.isEmpty(text.title)) 		status.errors.push(`title is empty (${text.language.toLowerCase()})`);
			if (text.title.length > 100) 				status.warnings.push(`title over 100 characters (${text.language.toLowerCase()})`);

			//b. check description
			if (Util.String.isEmpty(text.description))  status.errors.push(`description is empty (${text.language.toLowerCase()})`);

			//c. check the keywords
			if (text.keywords.length === 0) 			status.errors.push(`keywords are empty (${text.language.toLowerCase()})`);
			if (text.keywords.length > 10) 				status.warnings.push(`over 10 keywords (${text.language.toLowerCase()})`);
		}

		return status;
	}


	/**------------------------------------------------------
	 * Society6
	 * -------
	 * Title
	 *  - required, max 100 char
	 * Description
	 *  - required, max 250 char
	 * Keywords
	 *  - required, max 20 keywords, max 20 char in each keyword,
	 *  - replace space with '-'
	 */
	private checkSociety6(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Society6, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if english text is configured
		const textEn = this.getTextByLanguage(EnumLanguage.EN, data);
		if (!textEn) {
			status.errors.push('text was not defined (en)');
			return status;
		}

		//2 - check the title
		if (Util.String.isEmpty(textEn.title))			status.errors.push('title is empty (en)');
		if (textEn.title.length > 100)					status.warnings.push('title over 100 characters (en)');

		//3 - check the description
		if (Util.String.isEmpty(textEn.description))	status.errors.push('description is empty (en)');
		if (textEn.description.length > 250)			status.warnings.push('description over 250 characters (en)');

		//4 - check keywords
		if (textEn.keywords.length === 0)				status.errors.push('keywords are empty (en)');
		if (textEn.keywords.length > 20) 				status.warnings.push(`over 20 keywords (en)`);
		if (textEn.keywords.length !== 0 && Util.Keywords.marginalLength(textEn.keywords).max > 20) status.warnings.push(`over 20 character in a keyword (en)`);

		return status;
	}


	/**------------------------------------------------------
	 * Spreadshirt US and EU
	 * ---------------------
	 * Title
	 *  - required, max 50 char
	 * Description
	 *  - required, max 200 char
	 * Keywords
	 *  - required, min 3 keywords, max 25 keywords, each kw max 50 char
	 */
	private checkSpreadshirtUs(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.SpreadshirtUS, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if english text is configured
		const textEn = this.getTextByLanguage(EnumLanguage.EN, data);
		if (!textEn) {
			status.errors.push('text was not defined (en)');
			return status;
		}

		//2 - check the title
		if (Util.String.isEmpty(textEn.title))			status.errors.push('title is empty (en)');
		if (textEn.title.length > 50) 					status.warnings.push('title over 50 characters (en)');

		//3 - check description
		if (Util.String.isEmpty(textEn.description))	status.errors.push('description is empty (en)');
		if (textEn.description.length > 200)			status.warnings.push('description over 200 characters (en)');

		//4 - check the keywords
		if (textEn.keywords.length < 3)  				status.errors.push('less than 3 keywords (en)');
		if (textEn.keywords.length > 25) 				status.warnings.push(`over 25 keyword (en)`);
		if (textEn.keywords.length !== 0 && Util.Keywords.marginalLength(textEn.keywords).max > 50) status.warnings.push(`over 50 character in a keyword (en)`);

		return status;
	}

	private checkSpreadshirtEu(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.SpreadshirtEU, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if any language was created
		const validLanguages  : EnumLanguage[] = [EnumLanguage.EN, EnumLanguage.DE, EnumLanguage.FR, EnumLanguage.ES];
		const textsSpreadshirt = this.getTextsByLanguages(validLanguages, data);
		if (!textsSpreadshirt) {
			status.errors.push(`text not defined for any language (${validLanguages.join(', ').toLowerCase()})`);
			return status;
		}

		//2 - check if the texts are alright
		for (const text of textsSpreadshirt) {

			//a. check the title
			if (Util.String.isEmpty(text.title)) 		status.errors.push(`title is empty (${text.language.toLowerCase()})`);
			if (text.title.length > 50)					status.warnings.push(`title over 50 characters (${text.language.toLowerCase()})`);

			//b. check description
			if (Util.String.isEmpty(text.description))	status.errors.push(`description is empty (${text.language.toLowerCase()})`);
			if (text.description.length > 200)			status.warnings.push(`description over 200 characters (${text.language.toLowerCase()})`);

			//c. check the keywords
			if (text.keywords.length < 3)				status.errors.push(`less than 3 keywords (${text.language.toLowerCase()})`);
			if (text.keywords.length > 25) 				status.warnings.push(`over 25 keyword (${text.language.toLowerCase()})`);
			if (text.keywords.length !== 0 && Util.Keywords.marginalLength(text.keywords).max > 50) status.warnings.push(`over 50 character in a keyword (${text.language.toLowerCase()})`);
		}

		return status;
	}


	/**------------------------------------------------------
	 * Teepublic
	 * ---------
	 * Title
	 *  - required
	 * Description
	 *  - required, max 750 char
	 * Keywords
	 *  - required, min 2 keywords, max 15 keywords,
	 *  - each keyword less 39 chars
	 */
	private checkTeepublic(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Teepublic, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if english text is configured
		const textEn = this.getTextByLanguage(EnumLanguage.EN, data);
		if (!textEn) {
			status.errors.push('text was not defined (en)');
			return status;
		}

		//2 - check the title and description
		if (Util.String.isEmpty(textEn.title))			status.errors.push('title is empty (en)');
		if (Util.String.isEmpty(textEn.description))	status.errors.push('description is empty (en)');
		if (textEn.description.length > 750)			status.warnings.push(`description over 750 characters (en)`);

		//3 - check the keywords
		if (textEn.keywords.length < 2)					status.errors.push('less than 2 keywords (en)');
		if (textEn.keywords.length > 25) 				status.warnings.push(`over 25 keyword (en)`);
		if (textEn.keywords.length !== 0 && Util.Keywords.marginalLength(textEn.keywords).max > 39) status.warnings.push(`over 39 character in a keyword (en)`);

		return status;
	}


	/**------------------------------------------------------
	 * Teespring
	 * ---------
	 * Title
	 *  - required, max 40 chars
	 * Description
	 *  - required, max 1500 chars
	 */
	private checkTeespring(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Teespring, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if english text is configured
		const textEn = this.getTextByLanguage(EnumLanguage.EN, data);
		if (!textEn) {
			status.errors.push('text was not defined (en)');
			return status;
		}

		//2 - check the title
		if (Util.String.isEmpty(textEn.title))			status.errors.push('title is empty (en)');
		if (textEn.title.length > 40)			 		status.warnings.push('title over 40 characters (en)');

		//3 - check the description
		if (Util.String.isEmpty(textEn.description)) 	status.errors.push('description is empty (en)');
		if (textEn.description.length > 1500)			status.warnings.push(`description over 1500 characters (en)`);

		return status;
	}


	/**------------------------------------------------------
	 * Tostadora
	 * ---------
	 * Title
	 *  - required, max 40 chars, no special chars
	 * Description
	 *  - optional (NOT required)
	 * Keywords
	 *  - optional (NOT required), max 10 keywords, max 30 char per keyword
	 */
	private checkTostadora(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Tostadora, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if any language was created
		const validLanguages  : EnumLanguage[] = [EnumLanguage.EN, EnumLanguage.DE, EnumLanguage.FR, EnumLanguage.ES];
		const textsSpreadshirt = this.getTextsByLanguages(validLanguages, data);
		if (!textsSpreadshirt) {
			status.errors.push(`text not defined for any language (${validLanguages.join(', ').toLowerCase()})`);
			return status;
		}

		//2 - check if the texts are alright
		for (const text of textsSpreadshirt) {

			//a. check the title
			if (Util.String.isEmpty(text.title))	 status.errors.push(`title is empty (${text.language.toLowerCase()})`);
			if (text.title.length > 40)			 	 status.warnings.push(`title over 40 characters (${text.language.toLowerCase()})`);
			if (/[^A-Za-z0-9\s]/g.test(text.title))  status.warnings.push(`title contains special characters (${text.language.toLowerCase()})`);		// check for special chars in the title

			//b. check the keywords
			if (text.keywords.length > 10) 		 	 status.warnings.push(`over 10 keyword (${text.language.toLowerCase()})`);
			if (text.keywords.length !== 0 && Util.Keywords.marginalLength(text.keywords).max > 30) status.warnings.push(`over 30 character in a keyword (${text.language.toLowerCase()})`);
		}
		return status;
	}


	/**------------------------------------------------------
	 * Zazzle
	 * ------
	 * Title
	 *  - required, max 50 chars
	 * Description
	 *  - required
	 * Keywords
	 *  - required, max 10 keywords,
	 *  - min 3 char in keyword, max 50 char per keywords
	 *  - max 500 char for all keywords combined
	 */
	private checkZazzle(data: IPodDesign, isListingEmpty: boolean): IPlatformStatus {

		//0 - check if any global data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Zazzle, warnings: [], errors : [] };
		if (isListingEmpty) {
			status.errors.push('global listing is empty');
			return status;
		}

		//1 - check if english text is configured
		const textEn = this.getTextByLanguage(EnumLanguage.EN, data);
		if (!textEn) {
			status.errors.push('text was not defined (en)');
			return status;
		}

		//2 - check the title and description
		if (Util.String.isEmpty(textEn.title))			status.errors.push('title is empty (en)');
		if (textEn.title.length > 50)					status.warnings.push(`title over 50 characters (EN})`);
		if (Util.String.isEmpty(textEn.description))	status.errors.push('description is empty (en)');

		//3 - check the keywords
		if (textEn.keywords.length === 0) 				status.errors.push(`keywords are empty (en)`);
		if (textEn.keywords.length > 10) 				status.warnings.push(`over 10 keyword (en)`);
		if (textEn.keywords.length !== 0) {
			if (Util.Keywords.marginalLength(textEn.keywords).min < 3)  status.warnings.push(`shorter 3 character in a keyword (en)`);
			if (Util.Keywords.marginalLength(textEn.keywords).max > 50) status.warnings.push(`over 50 character in a keyword (en)`);
			if (textEn.keywords.join('').length > 500) 				 	status.warnings.push('keywords over 500 characters (en)');
		}

		return status;
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private getTextByLanguage(language: EnumLanguage, data: IPodDesign): IGlobalText | null {

		//0 - get the text by the language
		const texts = this.getTextsByLanguages([language], data);
		if (!texts || texts.length === 0) return null;

		//1 - return the result
		if (texts.length > 1) throw new Error(`GlobalListingCheckHelper => getTextByLanguage => FATAL ERROR: only one language should be found for the language of "${language}"`);
		return texts[0];
	}

	private getTextsByLanguages(languages: EnumLanguage[], data: IPodDesign): IGlobalText[] | null {

		//0 - get the texts based on the languages
		const langTexts: IGlobalText[] = (data.listing.global?.texts || []).filter((text: IGlobalText) => languages.includes(text.language));

		//1 - filter out the empty texts
		const filteredTexts: IGlobalText[] = langTexts.filter((text: IGlobalText) => !Util.String.areEmpty([text.title, text.description, text.keywords.join('')]));
		if (filteredTexts.length === 0) return null;

		//2 - return the result
		return filteredTexts;
	}
}
